import { useCallback, useRef, ChangeEvent, RefObject, useContext } from 'react';
import { Editor } from '@tiptap/react';
import { useTranslation } from 'react-i18next';
import { aiAssistantContext } from '../../../../state/AIAssistantContextProvider';
import { ACCEPTED_IMAGE_TYPES } from '../constants';

type UploadImageFunction = (file: File) => Promise<{ displayUrl: string }>;

interface UseImageUploadOptions {
  editor: RefObject<Editor | null> | Editor | null;
  uploadImage: UploadImageFunction;
}

export const useImageUpload = ({ editor, uploadImage }: UseImageUploadOptions) => {
  const { t } = useTranslation();
  const { setImageUploadError } = useContext(aiAssistantContext);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const editorInstance = editor && typeof editor === 'object' && 'current' in editor
      ? editor.current
      : editor;
    
    if (!editorInstance) {
      setImageUploadError(t('Editor not ready, please try again'));
      e.target.value = '';
      return;
    }
    
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setImageUploadError(t('Upload failed. This file format is not supported.'));
      e.target.value = '';
      return;
    }
    
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setImageUploadError(t('Upload failed. The file is too large (max. 10 MB).'));
      e.target.value = '';
      return;
    }
    
    try {
      const result = await uploadImage(file);
      
      const imageUrl = result.displayUrl;
      if (!imageUrl) {
        throw new Error('No displayUrl in response');
      }
      
      let success = editorInstance.chain().focus().setImageBlock({ src: imageUrl }).run();
      
      if (!success) {
        success = editorInstance.chain().focus().insertContent({
          type: 'imageBlock',
          attrs: { src: imageUrl }
        }).run();
      }
      
      if (!success) {
        throw new Error('Failed to insert image into editor');
      }
    } catch (error) {
      setImageUploadError(t('The image could not be inserted. Please try again.'));
    } finally {
      e.target.value = '';
    }
  }, [editor, setImageUploadError, t, uploadImage]);

  return {
    fileInputRef,
    handleImageUploadClick,
    handleFileChange,
  };
};

